import axios from 'axios';
import { STORAGE_KEYS } from '../constants';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:6000';

/**
 * Service for handling sensor data operations
 */
class SensorService {
  /**
   * Get authentication headers
   */
  static getAuthHeaders() {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Fetch all sensor data
   * @returns {Promise<Object>} Sensor data response
   */
  static async getAllSensorData() {
    try {
      // Check if user is authenticated
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (!token) {
        console.warn('No authentication token found, skipping sensor data fetch');
        return { status: 'error', message: 'Not authenticated' };
      }

      const response = await axios.get(`${API_BASE_URL}/api/bins/sensor-data`, {
        headers: this.getAuthHeaders(),
        timeout: 10000 // 10 second timeout
      });
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch sensor data:', error.message);
      return {
        status: 'error',
        data: [],
        message: 'Sensor service unavailable'
      };
    }
  }

  /**
   * Get sensor data for a specific bin
   * @param {string} binId - Bin ID to fetch data for
   * @returns {Promise<Object>} Sensor data response
   */
  static async getSensorDataByBinId(binId) {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (!token) {
        console.warn('No authentication token found, skipping sensor data fetch');
        return { status: 'error', message: 'Not authenticated' };
      }

      const response = await axios.get(`${API_BASE_URL}/api/bins/sensor-data/${binId}`, {
        headers: this.getAuthHeaders(),
        timeout: 10000
      });
      return response.data;
    } catch (error) {
      console.warn(`Failed to fetch sensor data for bin ${binId}:`, error.message);
      return {
        status: 'error',
        data: null,
        message: 'Sensor data not available'
      };
    }
  }

  /**
   * Check if sensor service is available
   * @returns {Promise<boolean>} True if service is available
   */
  static async checkSensorServiceStatus() {
    try {
      // Try to directly access sensor server first (no auth needed)
      const directResponse = await axios.get('http://localhost:5050/bins', {
        timeout: 2000
      });
      return true;
    } catch (directError) {
      // Fallback to main backend endpoint (requires auth)
      try {
        const response = await axios.get(`${API_BASE_URL}/api/bins/sensor-status`, {
          headers: this.getAuthHeaders(),
          timeout: 3000
        });
        return response.data.sensorServiceAvailable || false;
      } catch (error) {
        console.warn('Failed to check sensor service status:', error.message);
        return false;
      }
    }
  }

  /**
   * Get bins with sensor data (uses existing bins API that now includes sensor data)
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} Bins with sensor data
   */
  static async getBinsWithSensorData(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_BASE_URL}/api/bins${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url, {
        headers: this.getAuthHeaders(),
        timeout: 10000
      });

      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch bins with sensor data:', error.message);
      return [];
    }
  }

  /**
   * Get nearby bins with sensor data
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {number} radius - Radius in kilometers
   * @returns {Promise<Object>} Nearby bins response with sensor data
   */
  static async getNearbyBinsWithSensorData(lat, lng, radius = 10) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/bins/nearby`, {
        params: { lat, lng, radius },
        headers: this.getAuthHeaders(),
        timeout: 10000
      });

      return response.data;
    } catch (error) {
      console.error('Failed to fetch nearby bins with sensor data:', error.message);
      return {
        status: 'error',
        data: [],
        message: 'Failed to fetch nearby bins'
      };
    }
  }

  /**
   * Format sensor data for display
   * @param {Object} sensorData - Raw sensor data
   * @returns {Object} Formatted sensor data
   */
  static formatSensorData(sensorData) {
    if (!sensorData || !sensorData.hasSensor) {
      return {
        hasData: false,
        level: 'N/A',
        status: 'No Sensor',
        lastUpdate: 'N/A',
        color: '#gray-400'
      };
    }

    const level = sensorData.level;
    let status = 'Unknown';
    let color = '#gray-400';

    if (level !== null && level !== undefined) {
      if (level >= 80) {
        status = 'Full';
        color = '#ef4444'; // red
      } else if (level >= 60) {
        status = 'Nearly Full';
        color = '#f59e0b'; // amber
      } else if (level >= 30) {
        status = 'Half Full';
        color = '#eab308'; // yellow
      } else {
        status = 'Empty';
        color = '#22c55e'; // green
      }
    }

    return {
      hasData: true,
      level: level !== null ? `${level}%` : 'N/A',
      levelValue: level,
      status,
      color,
      lastUpdate: sensorData.lastSensorUpdate
        ? new Date(sensorData.lastSensorUpdate).toLocaleString()
        : 'N/A'
    };
  }

  /**
   * Get fill level color based on percentage
   * @param {number} level - Fill level percentage
   * @returns {string} Color code
   */
  static getFillLevelColor(level) {
    if (level >= 80) return '#ef4444'; // red
    if (level >= 60) return '#f59e0b'; // amber
    if (level >= 30) return '#eab308'; // yellow
    return '#22c55e'; // green
  }

  /**
   * Get fill level status text
   * @param {number} level - Fill level percentage
   * @returns {string} Status text
   */
  static getFillLevelStatus(level) {
    if (level >= 80) return 'Full';
    if (level >= 60) return 'Nearly Full';
    if (level >= 30) return 'Half Full';
    return 'Empty';
  }

  /**
   * Start real-time polling for sensor data
   * @param {Function} callback - Callback function to handle updates
   * @param {number} interval - Polling interval in milliseconds (default: 30 seconds)
   * @returns {number} Interval ID for clearing
   */
  static startRealTimePolling(callback, interval = 30000) {
    const pollSensorData = async () => {
      try {
        const data = await this.getAllSensorData();
        callback(data);
      } catch (error) {
        console.warn('Real-time polling error:', error.message);
      }
    };

    // Initial fetch
    pollSensorData();

    // Set up interval
    return setInterval(pollSensorData, interval);
  }

  /**
   * Stop real-time polling
   * @param {number} intervalId - Interval ID to clear
   */
  static stopRealTimePolling(intervalId) {
    if (intervalId) {
      clearInterval(intervalId);
    }
  }
}

export default SensorService;
